// @ts-strict-ignore
/* eslint-disable @typescript-eslint/no-use-before-define */

import partition from 'lodash/partition';

import {
  CohortItem,
  isEventBasedEntry,
  isInlineCohortItem,
  isMetricEntry,
  isUserBasedEntry,
  MetricEntry,
  MetricEntryBehavior,
  QueryCohortEntry,
  QueryEntry,
  QueryEventEntry,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {ResourceType} from 'common/report/constants';
import {dataGroupIDsEqual} from 'common/util/index';
import {hasSCDFiltersFromOtherDataGroup, hasUserFilters} from 'common/util/custom_events';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {
  CohortSegment,
  isCohortSegment,
  isCohortSegmentItem,
  isEventSegment,
  isInlineCohortSegment,
  isUserSegment,
  Segment,
} from 'common/query-builder/query-block/segment-menu/types';
import type {CustomEventDefinition, CustomProperty} from 'common/report/util/saved-entities/types';
import {reporter} from 'common/report/error-reporting';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {isBehavioralCustomProperty} from 'common/widgets/custom-property-modal/util';
import {
  isAggregateUserFreqByGroupSegmentation,
  isBehaviorSegmentation,
} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {Segmentation} from 'common/types/reports/query';
import type {BehaviorMetric} from 'common/report/insights/util/types';
import {MetricType, TopLevelMetricType} from 'common/types/reports/bookmark';
import type {MetricsResource} from 'common/report/model-resources/metrics';
import type {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {isScdPropertyName} from 'common/report/util/properties';
import {getResolvedFormulaEntry} from 'common/util/formulas';
import {Property} from 'common/query-builder/query-block/properties-menu/types';
import {BehaviorShowClause} from 'common/report/insights/models/show-clause';
import {CustomPropertiesResource} from 'common/report/model-resources/custom-properties';
import {DataGroup} from 'common/widgets/data-group-selector/types';

import {QueryBuilderSelection} from '../types';

type SanitizeDeps = {
  customEvents: CustomEventDefinition[];
  customProperties: CustomPropertiesResource;
  colocatedDataGroups: DataGroup[];
  metricsResource: MetricsResource;
  behaviorsResource: BehaviorsResource;
};

/**
 * For global data group queries. Removes invalid entries for the selected data group and updates dataGroupId fields on
 * the remaining entries
 */
export function setQueryDataGroup({
  query,
  dataGroupId,
  ...sanitizeDeps
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
} & SanitizeDeps): QueryBuilderSelection {
  const sanitizedQuery = sanitizeQueryForDataGroup({query, dataGroupId, ...sanitizeDeps});
  return setQueryDataGroupProps({query: sanitizedQuery, dataGroupId});
}

function metricHasCustomBehaviorWithInvalidFilters({
  metric,
  customEvents,
  dataGroupId,
}: {
  metric: MetricEntry;
  customEvents: CustomEventDefinition[];
  dataGroupId: string;
}): boolean {
  const customEventIds = new Set<number>();

  metric.behavior.behaviors.forEach((behavior) => {
    const hasEventComparison = !!behavior.behaviors;
    if (hasEventComparison) {
      behavior.behaviors.forEach((beh) => {
        if (beh.type === MetricType.CustomEvent) {
          customEventIds.add(beh.id);
        }
      });
    }
    if (behavior.type === MetricType.CustomEvent) {
      customEventIds.add(behavior.id);
    }
  });

  if (customEventIds.size === 0) {
    return false;
  }
  if (!customEvents) {
    reporter.error(`customEvents argument missing`);
    return true;
  }

  return Array.from(customEventIds).some((id) => {
    const customEvent = customEvents.find((cb) => cb.id === id);
    return (
      customEvent &&
      hasSCDFiltersFromOtherDataGroup({
        customEvent,
        customEvents,
        dataGroupId,
      })
    );
  });
}

function metricHasInvalidFilterForDataGroup({
  behaviors,
  dataGroupId,
  metricLevelDataGroups,
  ...sanitizeDeps
}: {
  behaviors: MetricEntryBehavior[];
  dataGroupId?: string;
  metricLevelDataGroups: boolean;
} & SanitizeDeps) {
  return behaviors.some((beh) => {
    const hasEventComparison = !!beh.behaviors;
    if (hasEventComparison) {
      return metricHasInvalidFilterForDataGroup({
        behaviors: beh.behaviors,
        dataGroupId,
        metricLevelDataGroups,
        ...sanitizeDeps,
      });
    }
    return beh.filters?.some((filter) =>
      filterIsInvalidForDataGroup({
        filter,
        dataGroupId,
        metricLevelDataGroups,
        ...sanitizeDeps,
      }),
    );
  });
}

function isCustomEventWithUserFilters(eventEntry: QueryEventEntry, customEvents: CustomEventDefinition[]): boolean {
  if (!eventEntry.event.custom) {
    return false;
  }
  if (!customEvents) {
    reporter.error(`customEvents argument missing`);
    return true;
  }

  const customEvent = customEvents.find((ce) => ce.id === eventEntry.event.id);
  return !customEvent || hasUserFilters(customEvent, customEvents);
}

/**
 * Checks if a cohort entry is invalid for the given data group context.
 * When dataGroupId is undefined (User), cohorts from colocated data groups are also valid.
 */
export function cohortEntryIsInvalidForDataGroup({
  item,
  colocatedDataGroups = [],
  dataGroupId,
}: {
  item: QueryCohortEntry | CohortSegment;
  colocatedDataGroups?: DataGroup[];
  dataGroupId?: string;
}) {
  if (!isCohortSegmentItem(item)) {
    item = convertCohortItemToCohortSegment(item.event);
  }
  const cohortDataGroupId = getCohortSegmentDataGroupId(item);

  // cohorts from colocated data groups are valid for User
  if (!dataGroupId && colocatedDataGroups.length > 0) {
    const isFromColocatedGroup = colocatedDataGroups.some((dataGroup) =>
      dataGroupIDsEqual(cohortDataGroupId, dataGroup.groupID),
    );
    if (isFromColocatedGroup) {
      return false;
    }
  }
  // If the cohort's data group matches the given data group, it's valid
  return !dataGroupIDsEqual(cohortDataGroupId, dataGroupId);
}

export function isClauseInvalidForDataGroup({clause, dataGroupId}: {clause: BehaviorMetric; dataGroupId: string}) {
  const {type, name, id, raw_cohort: rawCohort} = clause.behavior;
  const {property} = clause.measurement;
  return (
    (type === MetricType.Cohort &&
      cohortEntryIsInvalidForDataGroup({
        item: {
          cohort: {
            id,
            name,
            raw_cohort: rawCohort,
          },
        },
        dataGroupId,
      })) ||
    // Detects People property ("All People" is supported by group-level analysis)
    (type === MetricType.People && !!property && !!dataGroupId)
  );
}

type RemovedQueryBuilderSelection = Pick<QueryBuilderSelection, `entries` | `exclusionSteps` | `filters` | `groupBy`>;

/**
 * Removes invalid entries for queries with metric-level data groups. This is
 * the equivalent of `setQueryDataGroup` / `sanitizeQueryForDataGroup` for
 * queries with a single data group.
 *
 * We return the final sanitized set as well as any entries that were fully
 * removed for improved logging and debugging.
 * */
export function sanitizeSelectionForDataGroups({
  query,
  ...sanitizeDeps
}: {query: QueryBuilderSelection} & SanitizeDeps): {
  final: QueryBuilderSelection;
  removed: RemovedQueryBuilderSelection | null;
} {
  // NOTE: Flows still uses a global dataGroupId on `query.dataGroupId`.
  const dataGroupIds: Set<string | null> = query.dataGroupId
    ? new Set([query.dataGroupId])
    : query.entries.reduce((acc, entry) => {
        const entryDataGroupIds = getEntryDataGroupId({entry});
        entryDataGroupIds.forEach((dataGroupId) => acc.add(dataGroupId));
        return acc;
      }, new Set<string | null>());

  const entries = sanitizeEventsForDataGroup({
    queryEntries: query.entries,
    metricLevelDataGroups: true,
    ...sanitizeDeps,
  });

  const exclusionSteps = sanitizeEventsForDataGroup({
    queryEntries: query.exclusionSteps,
    metricLevelDataGroups: true,
    ...sanitizeDeps,
  });

  const filters = sanitizeFiltersForDataGroups({
    filters: query.filters,
    dataGroupIds,
    metricLevelDataGroups: true,
    ...sanitizeDeps,
  });

  const groupBy = sanitizeGroupByForDataGroups({
    customProperties: sanitizeDeps.customProperties,
    colocatedDataGroups: sanitizeDeps.colocatedDataGroups,
    dataGroupIds,
    metricLevelDataGroups: true,
    segments: query.groupBy,
  });

  const final: QueryBuilderSelection = {
    ...query,
    entries: entries.final,
    exclusionSteps: exclusionSteps.final as QueryEventEntry[],
    filters: filters.final,
    groupBy: groupBy.final,
  };

  const removed: RemovedQueryBuilderSelection = {
    entries: entries.removed,
    exclusionSteps: exclusionSteps.removed as QueryEventEntry[],
    filters: filters.removed,
    groupBy: groupBy.removed,
  };

  return {
    final,
    // Return null if nothing was removed.
    removed: Object.values(removed).every((arr) => arr.length === 0) ? null : removed,
  };
}

/** For global data group queries */
function sanitizeQueryForDataGroup({
  query,
  dataGroupId,
  ...sanitizeDeps
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
} & SanitizeDeps): QueryBuilderSelection {
  const entries = sanitizeEventsForDataGroup({
    queryEntries: query.entries,
    dataGroupId,
    metricLevelDataGroups: false,
    ...sanitizeDeps,
  });

  const exclusionSteps = sanitizeEventsForDataGroup({
    queryEntries: query.exclusionSteps,
    dataGroupId,
    metricLevelDataGroups: false,
    ...sanitizeDeps,
  });

  const filters = sanitizeFiltersForDataGroups({
    filters: query.filters,
    dataGroupIds: new Set([dataGroupId]),
    metricLevelDataGroups: false,
    ...sanitizeDeps,
  });

  const groupBy = sanitizeGroupByForDataGroups({
    customProperties: sanitizeDeps.customProperties,
    colocatedDataGroups: sanitizeDeps.colocatedDataGroups,
    dataGroupIds: new Set([dataGroupId]),
    metricLevelDataGroups: false,
    segments: query.groupBy,
  });

  return {
    ...query,
    entries: entries.final,
    exclusionSteps: exclusionSteps.final as QueryEventEntry[],
    filters: filters.final,
    groupBy: groupBy.final,
  };
}

function sanitizeMetricBehaviorsForDataGroup({
  behaviors,
  dataGroupId,
  metricLevelDataGroups,
  ...sanitizeDeps
}: {
  behaviors: MetricEntryBehavior[];
  dataGroupId?: string;
  metricLevelDataGroups: boolean;
} & SanitizeDeps) {
  return behaviors.map((beh) => {
    const hasEventComparison = !!beh.behaviors;
    if (hasEventComparison) {
      return {
        ...beh,
        behaviors: sanitizeMetricBehaviorsForDataGroup({
          behaviors: beh.behaviors,
          dataGroupId,
          metricLevelDataGroups,
          ...sanitizeDeps,
        }),
      };
    }
    const filters =
      beh.filters?.filter(
        (filter) =>
          !filterIsInvalidForDataGroup({
            filter,
            dataGroupId,
            metricLevelDataGroups,
            ...sanitizeDeps,
          }),
      ) || [];
    return {
      ...beh,
      filters,
    };
  });
}

function sanitizeEntryForDataGroup({
  entry,
  dataGroupId,
  metricLevelDataGroups,
  ...sanitizeDeps
}: {
  entry: QueryEntry | MetricEntry;
  dataGroupId?: string;
  metricLevelDataGroups: boolean;
} & SanitizeDeps): QueryEntry | MetricEntry | null {
  let updatedEntry: QueryEntry | MetricEntry;
  if (metricLevelDataGroups) {
    // TODO: Should we use a set here as well?
    const dataGroupIds = getEntryDataGroupId({entry});
    dataGroupId = dataGroupIds.size ? dataGroupIds.values().next().value : null;
  }

  if (isEventBasedEntry(entry) || isUserBasedEntry(entry)) {
    if (isMetricEntry(entry)) {
      const {behavior, measurement} = entry;
      updatedEntry = {
        ...entry,
        behavior: {
          ...behavior,
          behaviors: sanitizeMetricBehaviorsForDataGroup({
            behaviors: behavior.behaviors,
            dataGroupId,
            metricLevelDataGroups,
            ...sanitizeDeps,
          }),
        },
        measurement: {
          ...measurement,
          property: propertyIsInvalidForDataGroup(measurement.property, dataGroupId) ? null : measurement.property,
        },
      };
    } else {
      updatedEntry = {
        ...entry,
        filters:
          entry.filters?.filter((filter) => {
            return !filterIsInvalidForDataGroup({filter, dataGroupId, metricLevelDataGroups, ...sanitizeDeps});
          }) || [],
        property: propertyIsInvalidForDataGroup(entry.property, dataGroupId) ? null : entry.property,
      };
    }
  } else {
    updatedEntry = entry;
  }

  const invalid = queryEntryIsInvalidForDataGroup({
    queryEntry: updatedEntry,
    dataGroupId,
    metricLevelDataGroups,
    ...sanitizeDeps,
  });
  return invalid ? null : updatedEntry;
}

function sanitizeEventsForDataGroup({
  queryEntries,
  dataGroupId,
  metricLevelDataGroups,
  ...sanitizeDeps
}: {
  queryEntries: Array<QueryEntry | MetricEntry>;
  dataGroupId?: string;
  /** retrieve dataGroupId from each entry (instead of the dataGroupId param, which is for a single global data group id) */
  metricLevelDataGroups: boolean;
} & SanitizeDeps): {
  final: Array<QueryEntry | MetricEntry>;
  removed: Array<QueryEntry | MetricEntry>;
} {
  // remove invalid event filters and property aggregations
  const sanitizedQueryEntries = queryEntries.map((entry) => {
    const sanitized = sanitizeEntryForDataGroup({
      entry,
      dataGroupId,
      metricLevelDataGroups,
      ...sanitizeDeps,
    });
    const valid = Boolean(sanitized);
    return {
      valid,
      entry: valid ? sanitized : entry,
    };
  });

  const [valid, invalid] = partition(sanitizedQueryEntries, (wrappedEntry) => wrappedEntry.valid);
  return {
    final: valid.map((wrappedEntry) => wrappedEntry.entry),
    removed: invalid.map((wrappedEntry) => wrappedEntry.entry),
  };
}

function queryEntryIsInvalidForDataGroup({
  queryEntry,
  dataGroupId,
  metricLevelDataGroups,
  ...sanitizeDeps
}: {
  queryEntry: QueryEntry | MetricEntry;
  dataGroupId?: string;
  metricLevelDataGroups: boolean;
} & SanitizeDeps): boolean {
  switch (queryEntry.type) {
    case TopLevelMetricType.Warehouse:
      return false;
    // @todo(metrics): Add tests to b2b-metrics.stories.tsx once all cases nailed down.
    case TopLevelMetricType.NewFormulaEntry:
      return queryEntry.referencedMetrics?.some((c) =>
        queryEntryIsInvalidForDataGroup({queryEntry: c, dataGroupId, metricLevelDataGroups, ...sanitizeDeps}),
      );
    case TopLevelMetricType.NewMetricEntry:
      // Simple, Funnels, Retention
      if (queryEntry.behavior.behaviors) {
        // Check if custom event filters are valid.
        const invalidCustomFilters = metricHasCustomBehaviorWithInvalidFilters({
          metric: queryEntry,
          customEvents: sanitizeDeps.customEvents,
          dataGroupId,
        });
        // Check if each behavior's inlined filters are valid.
        // This also makes sure to recursively check for event comparison.
        const invalidInlineFilters = metricHasInvalidFilterForDataGroup({
          behaviors: queryEntry.behavior.behaviors,
          dataGroupId,
          metricLevelDataGroups,
          ...sanitizeDeps,
        });

        // Check if any of the custom events have user filters.
        let invalidCustomEvents = false;
        if (queryEntry.behavior.type === MetricType.Simple) {
          invalidCustomEvents = queryEntry.behavior.behaviors
            .filter((b) => b.type === MetricType.CustomEvent)
            .some((someEvent) => {
              const customEventDefinition = sanitizeDeps.customEvents.find((ce) => ce.id === someEvent.id);
              // @todo(metrics): Here with `!customEventDefinition` we're essentially validating the existinence of a custom event.
              // Should we be doing this in hasValidMetricBookmarkShowClauses or something as well?
              return !customEventDefinition;
            });
        }

        return invalidCustomFilters || invalidInlineFilters || invalidCustomEvents;
      }

      // @todo(metrics): When we add profile metrics add this.
      // For profile types
      // // Check if the profile property is valid (profilePropertyIsInvalidForDataGroup)

      // @todo(metrics): When we add cohort metrics add this.
      // For cohort type
      // // Just check if data group id matches (cohortEntryIsInvalidForDataGroup)

      return Boolean(dataGroupId);

    // TODO: Once new metric entries are GA'd we can delete all the cases below here.
    case TopLevelMetricType.MetricEntry:
      return (
        (dataGroupId &&
          metricHasCustomBehaviorWithInvalidFilters({
            metric: queryEntry,
            customEvents: sanitizeDeps.customEvents,
            dataGroupId,
          })) ||
        (dataGroupId &&
          metricHasInvalidFilterForDataGroup({
            behaviors: queryEntry.behavior.behaviors,
            dataGroupId,
            metricLevelDataGroups,
            ...sanitizeDeps,
          })) ||
        (queryEntry.measurement.property && propertyIsInvalidForDataGroup(queryEntry.measurement.property, dataGroupId))
      );
    case ResourceType.Event:
    case SelectorTypeDefinitions.FlowsEvent:
    case SelectorTypeDefinitions.Behavior: {
      return (
        // Metric entries allow custom events whose filters reference user properties, because the backend inlines
        // those into the event selector and evaluates them per user. That is only verified for the Insights
        // translation path, so entries belonging to other reports keep the restriction.
        (dataGroupId && isCustomEventWithUserFilters(queryEntry as QueryEventEntry, sanitizeDeps.customEvents)) ||
        queryEntry.filters?.some((filter) =>
          filterIsInvalidForDataGroup({
            filter,
            dataGroupId,
            metricLevelDataGroups,
            ...sanitizeDeps,
          }),
        ) ||
        (queryEntry.property && propertyIsInvalidForDataGroup(queryEntry.property, dataGroupId))
      );
    }
    case ResourceType.Formula: {
      return getResolvedFormulaEntry({
        metricsResource: sanitizeDeps.metricsResource,
        behaviorsResource: sanitizeDeps.behaviorsResource,
        formulaEntry: queryEntry,
      })?.event?.referencedMetrics?.some((c) =>
        isClauseInvalidForDataGroup({
          clause: c as BehaviorMetric,
          dataGroupId,
        }),
      );
    }
    case SelectorTypeDefinitions.CohortGroup:
    case ResourceType.User: {
      const isAllPeople = `$all_people` === queryEntry.event.value;
      return (
        (!isAllPeople &&
          queryEntry.property &&
          profilePropertyIsInvalidForDataGroup({
            profilePropertyDataGroupId: queryEntry.dataGroupId,
            profilePropertyName: queryEntry.property.name,
            dataGroupId,
          })) ||
        queryEntry.filters?.some((filter) =>
          filterIsInvalidForDataGroup({
            filter,
            dataGroupId,
            metricLevelDataGroups,
            ...sanitizeDeps,
          }),
        )
      );
    }
    case ResourceType.Cohort:
      return cohortEntryIsInvalidForDataGroup({item: queryEntry, dataGroupId});
    case SelectorTypeDefinitions.GroupedEvent:
      return queryEntry.event.some((entry) =>
        queryEntryIsInvalidForDataGroup({
          queryEntry: entry,
          dataGroupId,
          metricLevelDataGroups,
          ...sanitizeDeps,
        }),
      );
    default:
      // Log unhandled cases and default to marking as invalid for non-null data group
      reporter.error(`Unexpected QueryEntry.type: ${JSON.stringify(queryEntry)}`);
      return !!dataGroupId;
  }
}

function filterIsInvalidForDataGroup({
  filter,
  dataGroupId,
  metricLevelDataGroups,
  ...sanitizeDeps
}: {
  filter: Partial<PropertyFilter>;
  dataGroupId?: string;
  metricLevelDataGroups: boolean;
} & SanitizeDeps): boolean {
  switch (filter.resourceType) {
    case ResourceType.User: {
      if (filter.customProperty && isBehavioralCustomProperty(filter.customProperty)) {
        const {behavior} = filter.customProperty;
        return queryEntryIsInvalidForDataGroup({
          queryEntry: {
            ...behavior,
            type: SelectorTypeDefinitions.Behavior,
            filters: (behavior?.filters ?? []).map((segmentation) => new PropertyFilter({...segmentation})),
          },
          dataGroupId,
          metricLevelDataGroups,
          ...sanitizeDeps,
        });
      }

      if (!dataGroupId && isAggregateUserFreqByGroupSegmentation(filter as PropertyFilter)) {
        return true;
      }

      // TODO remove when behaviors are no longer user resource type
      if (isBehaviorSegmentation(filter as Segmentation)) {
        return false;
      }

      if (filter.customProperty || filter.customPropertyId) {
        return customPropertyIsInvalidForDataGroup({
          dataGroupId,
          customProperty: filter.customProperty,
          customPropertyId: filter.customPropertyId,
          customProperties: sanitizeDeps.customProperties,
        });
      }

      return profilePropertyIsInvalidForDataGroup({
        profilePropertyDataGroupId: filter.dataGroupId,
        profilePropertyName: filter.propertyName,
        dataGroupId,
      });
    }
    case ResourceType.Cohort:
      // TODO Delete the return true case when metricLevelDataGroups is turned on everywhere
      // I'm not sure this conditional is needed, but `true` was added to always remove cohorts when switching
      // datagroups to fix a SRFE and I don't want to cause a regression https://github.com/mixpanel/analytics/pull/57453/files
      return metricLevelDataGroups
        ? (filter.filterValue as CohortSegment[]).some((s) =>
            cohortEntryIsInvalidForDataGroup({
              item: s,
              dataGroupId,
              colocatedDataGroups: sanitizeDeps.colocatedDataGroups,
            }),
          )
        : true;
    case ResourceType.Event:
    case ResourceType.Metric:
    case ResourceType.Other:
    case ResourceType.Formula:
      if (filter.customProperty || filter.customPropertyId) {
        return customPropertyIsInvalidForDataGroup({
          dataGroupId,
          customProperty: filter.customProperty,
          customPropertyId: filter.customPropertyId,
          customProperties: sanitizeDeps.customProperties,
        });
      }
      return false;
    case ResourceType.RuntimeProperty:
      return false;
  }
}

function sanitizeFiltersForDataGroups({
  filters,
  dataGroupIds,
  metricLevelDataGroups,
  ...sanitizeDeps
}: {
  filters: Array<Partial<PropertyFilter>>;
  dataGroupIds: Set<string | null>;
  metricLevelDataGroups: boolean;
} & SanitizeDeps): {
  final: Array<Partial<PropertyFilter>>;
  removed: Array<Partial<PropertyFilter>>;
} {
  const [valid, invalid] = partition(filters, (filter) => {
    for (const dataGroupId of dataGroupIds) {
      if (
        filterIsInvalidForDataGroup({
          filter,
          dataGroupId,
          metricLevelDataGroups: true,
          colocatedDataGroups: sanitizeDeps.colocatedDataGroups,
          ...sanitizeDeps,
        })
      ) {
        return false;
      }
    }
    return true;
  });
  return {
    final: valid,
    removed: invalid,
  };
}

function sanitizeGroupByForDataGroups({
  customProperties,
  colocatedDataGroups,
  dataGroupIds,
  metricLevelDataGroups,
  segments,
}: {
  customProperties: CustomPropertiesResource;
  colocatedDataGroups: DataGroup[];
  dataGroupIds: Set<string | null>;
  metricLevelDataGroups: boolean;
  segments: Segment[];
}): {final: Segment[]; removed: Segment[]} {
  // require a non-null data group for PQA queries. we may need to restrict this further to only allow one unique data group
  const allowPQAQueries = dataGroupIds.size && !dataGroupIds.has(null);
  const [valid, invalid] = partition(segments, (s) => {
    for (const dataGroupId of dataGroupIds) {
      if (
        segmentItemIsInvalidForDataGroup({
          segment: s,
          colocatedDataGroups,
          dataGroupId,
          customProperties,
          metricLevelDataGroups,
        })
      ) {
        return false;
      }
    }
    const isInvalidPQASegment = !allowPQAQueries && isUserSegment(s) && isAggregateUserFreqByGroupSegmentation(s.user);
    return !isInvalidPQASegment;
  });
  return {
    final: valid,
    removed: invalid,
  };
}

function segmentItemIsInvalidForDataGroup({
  segment,
  colocatedDataGroups,
  dataGroupId,
  customProperties,
  metricLevelDataGroups,
}: {
  segment: Segment;
  colocatedDataGroups: DataGroup[];
  dataGroupId: string;
  customProperties: CustomPropertiesResource;
  metricLevelDataGroups: boolean;
}): boolean {
  if (isUserSegment(segment) && (segment.user.customProperty || segment.user.customPropertyId)) {
    return customPropertyIsInvalidForDataGroup({
      dataGroupId,
      customProperty: segment.user.customProperty,
      customPropertyId: segment.user.customPropertyId,
      customProperties,
    });
  } else if (isUserSegment(segment) && !isBehaviorSegmentation(segment.user)) {
    // TODO remove once behaviors are not user resource type
    return [].concat(segment).some((seg) =>
      profilePropertyIsInvalidForDataGroup({
        profilePropertyDataGroupId: seg.user.dataGroupId,
        profilePropertyName: seg.user.propertyName,
        dataGroupId,
      }),
    );
  } else if (isCohortSegment(segment)) {
    // TODO Delete the return true case when metricLevelDataGroups is turned on everywhere
    // I'm not sure this conditional is needed, but `true` was added to always remove cohorts when switching
    // datagroups to fix a SRFE and I don't want to cause a regression https://github.com/mixpanel/analytics/pull/57467/files
    return metricLevelDataGroups
      ? segment.some((s) => cohortEntryIsInvalidForDataGroup({item: s, dataGroupId, colocatedDataGroups}))
      : true;
  }
  if (isEventSegment(segment) && (segment.event.customProperty || segment.event.customPropertyId)) {
    return customPropertyIsInvalidForDataGroup({
      dataGroupId,
      customProperty: segment.event.customProperty,
      customPropertyId: segment.event.customPropertyId,
      customProperties,
    });
  }
  return false;
}

function setEntriesForDataGroup({
  queryEntries,
  dataGroupId,
}: {
  queryEntries: Array<QueryEntry | MetricEntry>;
  dataGroupId?: string;
}): Array<QueryEntry | MetricEntry> {
  return queryEntries.map((entry) => {
    if (isMetricEntry(entry)) {
      const {behavior, measurement} = entry;
      if (dataGroupId === null && measurement.property) {
        delete measurement.property[`dataGroupId`];
      }
      return {
        ...entry,
        behavior: {
          ...behavior,
          dataGroupId,
        },
        measurement,
      };
    }
    if (dataGroupId === null && entry.property) {
      delete entry.property[`dataGroupId`];
    }
    return {
      ...entry,
      dataGroupId,
    };
  });
}

function setQueryDataGroupProps({
  query,
  dataGroupId,
}: {
  query: QueryBuilderSelection;
  dataGroupId?: string;
}): QueryBuilderSelection {
  return {
    ...query,
    entries: setEntriesForDataGroup({queryEntries: query.entries, dataGroupId}),
    exclusionSteps: query.exclusionSteps.map((entry) => ({...entry, dataGroupId})),
    dataGroupId,
  };
}

function customPropertyIsInvalidForDataGroup({
  dataGroupId,
  customProperty,
  customPropertyId,
  customProperties,
}: {
  dataGroupId: string;
  customProperty: CustomProperty;
  customPropertyId: string | number;
  customProperties: CustomPropertiesResource;
}) {
  function isInvalidForDataGroup(customProp: CustomProperty, customPropId: string | number) {
    const relevantCustomProp = customProp || customProperties.getOne(customPropId).value;
    // if the custom prop isn't in the model resource, it was most likely deleted. So there's no way to invalidate.
    if (!relevantCustomProp) {
      return false;
    }
    const composedProperties = relevantCustomProp.composedProperties;
    return Object.values(composedProperties).some((prop: Property) => {
      if (prop.customPropertyId) {
        return isInvalidForDataGroup(prop.customProperty, prop.customPropertyId);
      }

      // In group mode, custom prop is invalid if it has user props or group props from other data groups
      if (dataGroupId) {
        return prop.resourceType === ResourceType.User && prop.dataGroupId !== dataGroupId;
      }

      // In user mode, custom prop is valid if it has scd props from other data groups, as those scd properties will fallback to the standard group property.
      return false;
    });
  }

  return isInvalidForDataGroup(customProperty, customPropertyId);
}

export function profilePropertyIsInvalidForDataGroup({
  profilePropertyDataGroupId,
  profilePropertyName,
  dataGroupId,
}: {
  profilePropertyDataGroupId: string;
  profilePropertyName: string | string[];
  dataGroupId: string;
}) {
  if (typeof profilePropertyName === `string` && isScdPropertyName(profilePropertyName)) {
    // SCD properties are invalid for mismatched data groups, except for the empty data group for user queries.
    return dataGroupId && !dataGroupIDsEqual(profilePropertyDataGroupId, dataGroupId);
  } else if (profilePropertyName === `$mp_first_event_time`) {
    // First event time is a behavioral property, and the correct data group id-- global or metric-level, once that is
    // rolled out-- is applied at the query-api level if dataGroupId is blank. If dataGroupId is set, it should match.
    // We could remove this and always return false if we do a bookmark migration to remove dataGroupId from
    // $mp_first_event_time filters/breakdowns on old bookmarks.
    return Boolean(profilePropertyDataGroupId) && !dataGroupIDsEqual(profilePropertyDataGroupId, dataGroupId);
  } else if (dataGroupId) {
    // B2B analysis allows using all data groups' profile properties except for the empty "user" data group
    return !profilePropertyDataGroupId;
  }
  return false;
}

function convertCohortItemToCohortSegment(item: CohortItem): CohortSegment {
  if (isInlineCohortItem(item)) {
    return {cohort: {...item.value.cohort, negated: false}};
  }
  return {
    cohort: {
      id: item.value as number,
      name: item.label,
      negated: false,
      ...(item.data_group_id ? {data_group_id: item.data_group_id} : null),
    },
  };
}

function getCohortSegmentDataGroupId(segment: CohortSegment): string {
  if (isInlineCohortSegment(segment)) {
    return segment.cohort.raw_cohort.data_group_id;
  }
  return segment.cohort.data_group_id;
}

function propertyIsInvalidForDataGroup(propertyItem?: PropertySectionItem, dataGroupId?: string): boolean {
  return (
    !!propertyItem &&
    propertyItem.resourceType === ResourceType.User &&
    profilePropertyIsInvalidForDataGroup({
      profilePropertyDataGroupId: propertyItem.dataGroupId,
      profilePropertyName: propertyItem.name || propertyItem.value,
      dataGroupId,
    })
  );
}

export function getMetricDataGroupId(entry: BehaviorMetric | MetricEntry | BehaviorShowClause): string | null {
  if ([MetricType.People, MetricType.Cohort].includes(entry.behavior?.type)) {
    return entry.behavior?.dataGroupId || null;
  }
  return entry.measurement?.dataGroupId || null;
}

export function getEntryDataGroupId({entry}: {entry: QueryEntry | MetricEntry}): Set<string | null> {
  if (!entry) {
    return new Set();
  }
  if (entry.type === TopLevelMetricType.NewFormulaEntry) {
    const referencedMetrics = entry.referencedMetrics;
    return referencedMetrics ? new Set(referencedMetrics.map((m) => getMetricDataGroupId(m))) : new Set();
  } else if (isMetricEntry(entry)) {
    return new Set([getMetricDataGroupId(entry)]);
  }
  return new Set([entry.dataGroupId || null]);
}
